import axios from 'axios';
import { ReturnCodes } from '../utils/constants';
import { getAuthHeaders } from '../utils/authUtils';

class StatusService{

    async getServerVersion(){
        return new Promise(async (resolve, reject) => {
            try{
                await axios
                .get(process.env.REACT_APP_API_URL + `/api/v1/version`, {
                    headers: { 'Access-Control-Allow-Origin': '*', 
                    'Access-Control-Allow-Credentials': true },
                })
                .then(response => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(ReturnCodes.UNABLE_TO_READ_VERSION);
                })
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_READ_VERSION);
            }
        });
    };

    /**
     * Get processing status for videos
     * 
     * @param {string} timeframe - Time period to fetch (1h, 6h, 12h, 24h, 7d, 30d)
     * @param {string} status - Filter by status (in_progress, completed, all)
     * @param {string} email - User email
     * @param {string} authToken - Authentication token
     * @returns {Promise} - Promise resolving to API response
     */
    async getProcessingStatus(timeframe, status, email, authToken) {
        try {
            const params = new URLSearchParams();
            if (timeframe) params.append('timeframe', timeframe);
            if (status) params.append('status', status);
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/videos/processing-status`,
                {
                    headers: {
                        email: email,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                    params
                }
            );
            return response;
        } catch (error) {
            console.error('Error fetching processing status:', error);
            throw error;
        }
    }

    /**
     * Get MongoDB sync status
     * @returns {Promise} Promise with MongoDB sync status
     */
    async getMongoSyncStatus() {
        return new Promise(async (resolve, reject) => {
            try{
                await axios
                .get(process.env.REACT_APP_API_URL + `/api/v3/admin/mongo-sync/status`, 
                    { headers: getAuthHeaders() }
                )
                .then(response => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(ReturnCodes.UNABLE_TO_GET_MONGO_SYNC_STATUS);
                })
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_MONGO_SYNC_STATUS);
            }
        });
    };

    /**
     * Get MongoDB database size information
     * @returns {Promise} Promise with MongoDB size information
     */
    async getMongoDatabaseSize() {
        return new Promise(async (resolve, reject) => {
            try{
                await axios
                .get(process.env.REACT_APP_API_URL + `/api/v3/admin/mongo-sync/database-size`, 
                    { headers: getAuthHeaders() }
                )
                .then(response => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(ReturnCodes.UNABLE_TO_GET_MONGO_DATABASE_SIZE);
                })
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_MONGO_DATABASE_SIZE);
            }
        });
    };

    /**
     * Get MongoDB sync history
     * @returns {Promise} Promise with MongoDB sync history
     */
    async getMongoSyncHistory() {
        return new Promise(async (resolve, reject) => {
            try{
                await axios
                .get(process.env.REACT_APP_API_URL + `/api/v3/admin/mongo-sync/history`, 
                    { headers: getAuthHeaders() })
                .then(response => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(ReturnCodes.UNABLE_TO_GET_MONGO_SYNC_HISTORY);
                })
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_MONGO_SYNC_HISTORY);
            }
        });
    };

    /**
     * Trigger MongoDB sync
     * @returns {Promise} Promise with trigger response
     */
    async triggerMongoSync() {
        return new Promise(async (resolve, reject) => {
            try{
                await axios
                .post(process.env.REACT_APP_API_URL + `/api/v3/admin/mongo-sync/trigger`, {}, 
                    { headers: getAuthHeaders() }
                )
                .then(response => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(ReturnCodes.UNABLE_TO_TRIGGER_MONGO_SYNC);
                })
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_TRIGGER_MONGO_SYNC);
            }
        });
    };

    async getServerStatus() {
        try {
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/status/server`,
                { headers: getAuthHeaders(true) }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching server status:', error);
            throw error;
        }
    }

    /**
     * Reset the duplicate video count
     * @returns {Promise} Promise with reset response
     */
    async resetDuplicateVideoCount() {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v3/status/duplicate-video-alert`, 
                {}, 
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Error resetting duplicate video count:', error);
            throw error;
        }
    }
}

const statusServiceInstance = new StatusService();
export default statusServiceInstance;